import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { signOut } from 'aws-amplify/auth';
import { configureAmplify, cognitoHostedUILogin, waitForOAuthRedirect, getAccessToken } from '../services/auth';
import config from '../config';
import { setOnApiActivity } from '../services/api';

const UserContext = createContext(null);

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export function useUser() {
    return useContext(UserContext);
}

export function UserProvider({ children }) {
    const [user, setUser] = useState(null);
    const [layout, setLayoutState] = useState('Basic');
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);

    const setLayout = useCallback((newLayout) => {
        setLayoutState(newLayout);
    }, []);

    const logout = useCallback(async () => {
        // 1. Clear httpOnly auth cookies on the backend
        try {
            await fetch('/api/admin/auth/clear-session', {
                method: 'POST',
                credentials: 'include',
            });
        } catch (e) {
            // Ignore - we're clearing everything anyway
        }

        // 2. Clear Amplify's local token cache
        try {
            await signOut();
        } catch (e) {
            // Ignore - we handle the redirect explicitly below
        }

        setUser(null);
        setLayoutState('Basic');

        // 3. Redirect to Cognito logout to clear Cognito's session cookie
        const domain = config.COGNITO_DOMAIN;
        const clientId = config.COGNITO_APP_CLIENT_ID;
        const logoutUri = encodeURIComponent(window.location.origin);
        window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }, []);

    const checkSession = useCallback(async () => {
        try {
            const response = await fetch('/api/admin/auth/profile', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setUser(data);
                return true;
            }
        } catch (e) {
            // No session
        }
        return false;
    }, []);

    const setSessionFromTokens = useCallback(async (idToken, refreshToken) => {
        try {
            const response = await fetch('/api/admin/auth/set-session', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_token: idToken,
                    refresh_token: refreshToken,
                }),
            });
            if (response.ok) {
                const data = await response.json();
                setUser(data);
                return true;
            }
        } catch (e) {
            // Session creation failed
        }
        return false;
    }, []);

    const refreshSession = useCallback(async () => {
        try {
            const response = await fetch('/api/admin/auth/refresh-session', {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                if (data.email) {
                    setUser(data);
                }
                return true;
            }
        } catch (e) {
            // Refresh failed
        }
        return false;
    }, []);

    // Idle timeout - logout after 15 minutes of inactivity
    const idleTimerRef = useRef(null);

    const resetIdleTimer = useCallback(() => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }
        idleTimerRef.current = setTimeout(() => {
            logout();
        }, IDLE_TIMEOUT_MS);
    }, [logout]);

    useEffect(() => {
        if (!user) return;

        const events = ['click', 'keydown'];
        const handleActivity = () => resetIdleTimer();

        events.forEach(event => window.addEventListener(event, handleActivity));
        setOnApiActivity(handleActivity);
        resetIdleTimer();

        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
            setOnApiActivity(null);
            if (idleTimerRef.current) {
                clearTimeout(idleTimerRef.current);
            }
        };
    }, [user, resetIdleTimer]);

    // Initialize auth on mount
    useEffect(() => {
        const init = async () => {
            configureAmplify();

            // Check for OAuth error in URL params
            const urlParams = new URLSearchParams(window.location.search);
            const oauthError = urlParams.get('error');
            const oauthErrorDesc = urlParams.get('error_description');
            if (oauthError) {
                window.history.replaceState({}, document.title, window.location.pathname);
                setAuthError(oauthErrorDesc || oauthError);
                setLoading(false);
                return;
            }

            // First, check if we already have a valid session cookie
            const hasSession = await checkSession();
            if (hasSession) {
                setLoading(false);
                return;
            }

            // No cookie session — use Cognito OAuth flow
            try {
                const token = await waitForOAuthRedirect();
                if (token) {
                    // We got a Cognito token — send it to backend to set cookies
                    const { fetchAuthSession } = await import('aws-amplify/auth');
                    const session = await fetchAuthSession();
                    const idToken = session.tokens?.idToken?.toString();

                    if (idToken) {
                        const success = await setSessionFromTokens(idToken, null);
                        if (success) {
                            // Clean up URL params from OAuth redirect
                            window.history.replaceState({}, document.title, window.location.pathname);
                            setLoading(false);
                            return;
                        }
                    }
                }
                // No session — redirect to Cognito login
                cognitoHostedUILogin();
                return;
            } catch (err) {
                setAuthError(err.message || 'Sign-in failed');
                setLoading(false);
                return;
            }
        };

        init();
    }, [checkSession, setSessionFromTokens]);

    const value = {
        user,
        setUser,
        layout,
        setLayout,
        loading,
        authError,
        logout,
        checkSession,
        refreshSession,
        setSessionFromTokens,
        resetIdleTimer,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
}
